import React, { useState, useEffect } from 'react';
import { useAutoFill } from '../../hooks/useAutoFill';
import AutoFillFieldWrapper from '../common/AutoFillFieldWrapper';
import { isValidOptionalPhone } from '../../utils/phoneValidation';
import './MemberEditModal.css';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_ERROR =
  'Please enter a valid phone number (8–20 digits; spaces and dashes are OK; Australian numbers may start with 0).';

const MemberEditModal = ({
  isOpen,
  onClose,
  onSuccess,
  onUpdateMember,
  member,
  isProcessing,
  existingMembers = [],
  serverFieldErrors = {},
  onClearServerFieldError,
  onClearAllServerFieldErrors
}) => {
  const [formData, setFormData] = useState({
    organization_name: '',
    member_id: '',
    excel_id: '',
    excel_status: '',
    current_members_count: '',
    contact_person: '',
    applicant_role: '',
    other_name: '',
    other_role: '',
    other_email: '',
    other_phone: '',
    president_name: '',
    president_phone: '',
    president_email: '',
    secretary_name: '',
    secretary_phone: '',
    secretary_email: '',
    treasurer_name: '',
    treasurer_phone: '',
    treasurer_email: '',
    street_address_1: '',
    street_address_2: '',
    city: '',
    state: '',
    postcode: '',
    country: '',
    screening_frequency: '',
    screening_format: [],
    screening_venue: '',
    status: '',
    membership_status: '',
    is_active: true,
    membership_start_date: '',
    membership_end_date: '',
    email_subscribed: true,
    renewal: false,
    agreed_to_conditions: true,
    website_url: '',
    facebook_url: '',
    instagram_url: '',
    twitter_url: '',
    release_to_third_parties: false,
    publish_on_website: false,
    show_in_external_view: true,
    allow_distributor_access: true
  });
  const [errors, setErrors] = useState({});

  const getFieldError = (field) => errors[field] || serverFieldErrors[field] || null;

  // Auto-fill functionality
  const {
    suggestions,
    isLoading: autoFillLoading,
    showSuggestions,
    currentField,
    handleFieldChange: handleAutoFillFieldChange,
    applySuggestion,
    dismissSuggestions
  } = useAutoFill(existingMembers, {
    organization_name: { required: true, minLength: 2 },
    member_id: { required: true, minLength: 2 },
    excel_id: { minLength: 2 },
    member_count: { min: 1 },
    contact_person: { required: true, minLength: 2 },
    president_name: { required: true, minLength: 2 },
    president_email: { required: true, type: 'email' },
    president_phone: { required: false, type: 'phone' },
    secretary_name: { required: true, minLength: 2 },
    secretary_email: { required: true, type: 'email' },
    secretary_phone: { required: false, type: 'phone' },
    treasurer_name: { required: true, minLength: 2 },
    treasurer_email: { required: true, type: 'email' },
    treasurer_phone: { required: false, type: 'phone' },
    street_address_1: { required: true, minLength: 5 },
    street_address_2: { minLength: 2 },
    city: { required: true, minLength: 2 },
    state: { required: true },
    postcode: { required: true, pattern: /^\d{4}$/ },
    country: { required: true },
    screening_frequency: { minLength: 2 },
    screening_venue: { required: true, minLength: 2 },
    website_url: { type: 'url' },
    facebook_url: { type: 'url' },
    instagram_url: { type: 'url' },
    twitter_url: { type: 'url' }
  }, 'member');

  useEffect(() => {
    if (member) {
      setFormData({
        organization_name: member.organization_name || '',
        member_id: member.member_id || '',
        excel_id: member.excel_id || '',
        excel_status: member.excel_status || '',
        current_members_count: member.current_members_count || '',
        contact_person: member.contact_person || '',
        applicant_role: member.applicant_role || '',
        other_name: member.other_name || '',
        other_role: member.other_role || '',
        other_email: member.other_email || '',
        other_phone: member.other_phone || '',
        president_name: member.president_name || '',
        president_phone: member.president_phone || '',
        president_email: member.president_email || '',
        secretary_name: member.secretary_name || '',
        secretary_phone: member.secretary_phone || '',
        secretary_email: member.secretary_email || '',
        treasurer_name: member.treasurer_name || '',
        treasurer_phone: member.treasurer_phone || '',
        treasurer_email: member.treasurer_email || '',
        street_address_1: member.street_address_1 || '',
        street_address_2: member.street_address_2 || '',
        city: member.city || '',
        state: member.state || '',
        postcode: member.postcode || '',
        country: member.country || '',
        screening_frequency: member.screening_frequency || '',
        screening_format: member.screening_format || [],
        screening_venue: member.screening_venue || '',
        status: member.status || '',
        membership_status: member.membership_status || '',
        is_active: member.is_active !== undefined ? member.is_active : true,
        membership_start_date: member.membership_start_date || '',
        membership_end_date: member.membership_end_date || '',
        email_subscribed: member.email_subscribed !== undefined ? member.email_subscribed : true,
        renewal: member.renewal || false,
        agreed_to_conditions: member.agreed_to_conditions !== undefined ? member.agreed_to_conditions : true,
        website_url: member.website_url || '',
        facebook_url: member.facebook_url || '',
        instagram_url: member.instagram_url || '',
        twitter_url: member.twitter_url || '',
        release_to_third_parties: member.release_to_third_parties || false,
        publish_on_website: member.publish_on_website || false,
        show_in_external_view: member.show_in_external_view !== undefined ? member.show_in_external_view : true,
        allow_distributor_access: member.allow_distributor_access !== undefined ? member.allow_distributor_access : true
      });
    }
  }, [member]);

  const handleInputChange = (field, value) => {
    if (serverFieldErrors[field]) {
      onClearServerFieldError?.(field);
    }

    setFormData(prev => {
      const updatedFormData = {
        ...prev,
        [field]: value
      };

      // Clear error when user starts typing
      if (errors[field]) {
        setErrors(prev => ({
          ...prev,
          [field]: null
        }));
      }

      // Trigger auto-fill only for text-based fields
      if (typeof value === 'string' && value.trim().length >= 2) {
        handleAutoFillFieldChange(field, value, updatedFormData, (field, value) => {
          setFormData(prevFormData => ({
            ...prevFormData,
            [field]: value
          }));
        });
      }

      return updatedFormData;
    });
  };

  // "Other" contact person: the four detail fields become mandatory, matching the
  // Applications tab (ApplicationForm.js) — otherwise an "Other" contact could be
  // saved with none of their actual details captured.
  // Keyed on contact_person only (not applicant_role, which ApplicationForm also
  // accepts): many existing members have applicant_role='other' with a real exec
  // as their contact person, and requiring details there would block edits to
  // records that have no "Other" contact at all.
  const requiresOtherContactDetails = formData.contact_person === 'other';

  const validateForm = () => {
    const newErrors = {};

    // Required field validation
    if (!formData.organization_name.trim()) {
      newErrors.organization_name = 'Organization name is required';
    }

    if (!formData.member_id.trim()) {
      newErrors.member_id = 'Member ID is required';
    }

    // Email validation for contact emails
    const emailFields = ['president_email', 'secretary_email', 'treasurer_email'];
    emailFields.forEach(field => {
      if (formData[field] && !EMAIL_PATTERN.test(formData[field])) {
        newErrors[field] = 'Please enter a valid email address';
      }
    });

    // Phone validation (optional fields; allow Australian national format e.g. 04xx…)
    const phoneFields = ['president_phone', 'secretary_phone', 'treasurer_phone'];
    phoneFields.forEach(field => {
      if (formData[field] && !isValidOptionalPhone(formData[field])) {
        newErrors[field] = PHONE_ERROR;
      }
    });

    // "Other" contact person details are mandatory when "Other" is selected.
    if (requiresOtherContactDetails) {
      const otherName = formData.other_name.trim();
      if (!otherName) {
        newErrors.other_name = 'Contact name is required';
      } else if (otherName.length < 2) {
        newErrors.other_name = 'Contact name must be at least 2 characters';
      }

      const otherRole = formData.other_role.trim();
      if (!otherRole) {
        newErrors.other_role = 'Contact role is required';
      } else if (otherRole.length < 2) {
        newErrors.other_role = 'Contact role must be at least 2 characters';
      }

      const otherEmail = formData.other_email.trim();
      if (!otherEmail) {
        newErrors.other_email = 'Contact email is required';
      } else if (!EMAIL_PATTERN.test(otherEmail)) {
        newErrors.other_email = 'Please enter a valid email address';
      }

      const otherPhone = formData.other_phone.trim();
      if (!otherPhone) {
        newErrors.other_phone = 'Contact phone number is required';
      } else if (!isValidOptionalPhone(otherPhone)) {
        newErrors.other_phone = PHONE_ERROR;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onClearAllServerFieldErrors?.();

    if (!validateForm()) {
      return;
    }

    const save = onSuccess || onUpdateMember;
    if (save) save(member.id, formData);
  };

  const handleClose = () => {
    setErrors({});
    onClearAllServerFieldErrors?.();
    onClose();
  };

  if (!isOpen || !member) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-container member-edit-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2>Edit Member</h2>
          <button className="close-btn" onClick={handleClose} disabled={isProcessing}>
            ×
          </button>
        </div>

        {/* Body */}
        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            {/* Basic Information */}
            <div className="form-section">
              <h3>Basic Information</h3>
              <div className="form-row">
                <AutoFillFieldWrapper
                  fieldName="organization_name"
                  suggestions={suggestions}
                  isLoading={autoFillLoading}
                  showSuggestions={showSuggestions}
                  currentField={currentField}
                  onApplyField={(field, value) => applySuggestion(field, value, (field, value) => {
                    setFormData(prev => ({ ...prev, [field]: value }));
                  })}
                  onIgnoreField={(field) => applySuggestion(field, null, () => { })}
                >
                  <div className="form-group">
                    <label>Organization Name *</label>
                    <input
                      type="text"
                      className={getFieldError('organization_name') ? 'error' : ''}
                      placeholder="Enter organization name"
                      value={formData.organization_name}
                      onChange={(e) => handleInputChange('organization_name', e.target.value)}
                    />
                    {getFieldError('organization_name') && (
                      <span className="field-error">{getFieldError('organization_name')}</span>
                    )}
                  </div>
                </AutoFillFieldWrapper>

                <AutoFillFieldWrapper
                  fieldName="member_id"
                  suggestions={suggestions}
                  isLoading={autoFillLoading}
                  showSuggestions={showSuggestions}
                  currentField={currentField}
                  onApplyField={(field, value) => applySuggestion(field, value, (field, value) => {
                    setFormData(prev => ({ ...prev, [field]: value }));
                  })}
                  onIgnoreField={(field) => applySuggestion(field, null, () => { })}
                >
                  <div className="form-group">
                    <label>Member ID *</label>
                    <input
                      type="text"
                      className={getFieldError('member_id') ? 'error' : ''}
                      placeholder="Enter member ID"
                      value={formData.member_id}
                      onChange={(e) => handleInputChange('member_id', e.target.value)}
                    />
                    {getFieldError('member_id') && (
                      <span className="field-error">{getFieldError('member_id')}</span>
                    )}
                  </div>
                </AutoFillFieldWrapper>

                <div className="modern-form-group">
                  <label className="modern-form-label">
                    <span className="label-text">Excel ID</span>
                  </label>
                  <input
                    type="text"
                    className="modern-form-input"
                    placeholder="Enter Excel ID"
                    value={formData.excel_id}
                    onChange={(e) => handleInputChange('excel_id', e.target.value)}
                  />
                </div>

                <div className="modern-form-group">
                  <label className="modern-form-label">
                    <span className="label-text">Current Members Count</span>
                  </label>
                  <input
                    type="number"
                    className="modern-form-input"
                    placeholder="Enter member count"
                    value={formData.current_members_count}
                    onChange={(e) => handleInputChange('current_members_count', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Contact Information Section */}
            <div className="form-section">
              <h3 className="section-title">Contact Information</h3>
              <div className="form-grid">
                <div className="modern-form-group">
                  <label className="modern-form-label">
                    <span className="label-text">Contact Person</span>
                  </label>
                  <select
                    className="modern-form-select"
                    value={formData.contact_person}
                    onChange={(e) => handleInputChange('contact_person', e.target.value)}
                  >
                    <option value="">Select Contact Person</option>
                    <option value="president">President</option>
                    <option value="secretary">Secretary</option>
                    <option value="treasurer">Treasurer</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="modern-form-group">
                  <label className="modern-form-label">
                    <span className="label-text">Applicant Role</span>
                  </label>
                  <select
                    className="modern-form-select"
                    value={formData.applicant_role}
                    onChange={(e) => handleInputChange('applicant_role', e.target.value)}
                  >
                    <option value="">Select Role</option>
                    <option value="president">President</option>
                    <option value="secretary">Secretary</option>
                    <option value="treasurer">Treasurer</option>
                    <option value="other">Other</option>
                  </select>
                </div>

              </div>

              {/* Other contact person details — required when "Other" is selected */}
              {requiresOtherContactDetails && (
                <section className="other-contact-card" aria-labelledby="other-contact-title">
                  <h4 id="other-contact-title" className="other-contact-title">Other Contact Person</h4>
                  <div className="form-grid">
                    <div className="modern-form-group">
                      <label className="modern-form-label" htmlFor="member-edit-other-name">
                        <span className="label-text">Name *</span>
                      </label>
                      <input
                        id="member-edit-other-name"
                        type="text"
                        className={`modern-form-input ${getFieldError('other_name') ? 'error' : ''}`}
                        placeholder="Full name"
                        value={formData.other_name}
                        onChange={(e) => handleInputChange('other_name', e.target.value)}
                      />
                      {getFieldError('other_name') && (
                        <span className="modern-error-text">{getFieldError('other_name')}</span>
                      )}
                    </div>

                    <div className="modern-form-group">
                      <label className="modern-form-label" htmlFor="member-edit-other-role">
                        <span className="label-text">Role *</span>
                      </label>
                      <input
                        id="member-edit-other-role"
                        type="text"
                        className={`modern-form-input ${getFieldError('other_role') ? 'error' : ''}`}
                        placeholder="e.g. Committee Member"
                        value={formData.other_role}
                        onChange={(e) => handleInputChange('other_role', e.target.value)}
                      />
                      {getFieldError('other_role') && (
                        <span className="modern-error-text">{getFieldError('other_role')}</span>
                      )}
                    </div>

                    <div className="modern-form-group">
                      <label className="modern-form-label" htmlFor="member-edit-other-email">
                        <span className="label-text">Email *</span>
                      </label>
                      <input
                        id="member-edit-other-email"
                        type="email"
                        className={`modern-form-input ${getFieldError('other_email') ? 'error' : ''}`}
                        placeholder="email@example.com"
                        value={formData.other_email}
                        onChange={(e) => handleInputChange('other_email', e.target.value)}
                        autoComplete="email"
                      />
                      {getFieldError('other_email') && (
                        <span className="modern-error-text">{getFieldError('other_email')}</span>
                      )}
                    </div>

                    <div className="modern-form-group">
                      <label className="modern-form-label" htmlFor="member-edit-other-phone">
                        <span className="label-text">Phone Number *</span>
                      </label>
                      <input
                        id="member-edit-other-phone"
                        type="tel"
                        className={`modern-form-input ${getFieldError('other_phone') ? 'error' : ''}`}
                        placeholder="Phone number"
                        value={formData.other_phone}
                        onChange={(e) => handleInputChange('other_phone', e.target.value)}
                        autoComplete="tel"
                      />
                      {getFieldError('other_phone') && (
                        <span className="modern-error-text">{getFieldError('other_phone')}</span>
                      )}
                    </div>
                  </div>
                </section>
              )}
            </div>

            {/* Executive Information Section */}
            <div className="form-section">
              <h3 className="section-title">Executive Information</h3>
              <div className="exec-roles-grid">
                <section className="exec-role-card" aria-labelledby="exec-president-title">
                  <h4 id="exec-president-title" className="exec-role-title">President</h4>
                  <div className="exec-role-fields">
                    <AutoFillFieldWrapper
                      fieldName="president_name"
                      suggestions={suggestions}
                      isLoading={autoFillLoading}
                      showSuggestions={showSuggestions}
                      currentField={currentField}
                      onApplyField={(field, value) => applySuggestion(field, value, (field, value) => {
                        setFormData(prev => ({ ...prev, [field]: value }));
                      })}
                      onIgnoreField={(field) => applySuggestion(field, null, () => { })}
                    >
                      <div className="modern-form-group">
                        <label className="modern-form-label" htmlFor="member-edit-president-name">
                          <span className="label-text">Name</span>
                        </label>
                        <input
                          id="member-edit-president-name"
                          type="text"
                          className="modern-form-input"
                          placeholder="Full name"
                          value={formData.president_name}
                          onChange={(e) => handleInputChange('president_name', e.target.value)}
                        />
                      </div>
                    </AutoFillFieldWrapper>

                    <div className="modern-form-group">
                      <label className="modern-form-label" htmlFor="member-edit-president-email">
                        <span className="label-text">Email</span>
                      </label>
                      <input
                        id="member-edit-president-email"
                        type="email"
                        className={`modern-form-input ${getFieldError('president_email') ? 'error' : ''}`}
                        placeholder="email@example.com"
                        value={formData.president_email}
                        onChange={(e) => handleInputChange('president_email', e.target.value)}
                        autoComplete="email"
                      />
                      {getFieldError('president_email') && (
                        <span className="modern-error-text">{getFieldError('president_email')}</span>
                      )}
                    </div>

                    <div className="modern-form-group">
                      <label className="modern-form-label" htmlFor="member-edit-president-phone">
                        <span className="label-text">Phone</span>
                      </label>
                      <input
                        id="member-edit-president-phone"
                        type="tel"
                        className={`modern-form-input ${getFieldError('president_phone') ? 'error' : ''}`}
                        placeholder="Phone number"
                        value={formData.president_phone}
                        onChange={(e) => handleInputChange('president_phone', e.target.value)}
                        autoComplete="tel"
                      />
                      {getFieldError('president_phone') && (
                        <span className="modern-error-text">{getFieldError('president_phone')}</span>
                      )}
                    </div>
                  </div>
                </section>

                <section className="exec-role-card" aria-labelledby="exec-secretary-title">
                  <h4 id="exec-secretary-title" className="exec-role-title">Secretary</h4>
                  <div className="exec-role-fields">
                    <div className="modern-form-group">
                      <label className="modern-form-label" htmlFor="member-edit-secretary-name">
                        <span className="label-text">Name</span>
                      </label>
                      <input
                        id="member-edit-secretary-name"
                        type="text"
                        className="modern-form-input"
                        placeholder="Full name"
                        value={formData.secretary_name}
                        onChange={(e) => handleInputChange('secretary_name', e.target.value)}
                      />
                    </div>

                    <div className="modern-form-group">
                      <label className="modern-form-label" htmlFor="member-edit-secretary-email">
                        <span className="label-text">Email</span>
                      </label>
                      <input
                        id="member-edit-secretary-email"
                        type="email"
                        className={`modern-form-input ${getFieldError('secretary_email') ? 'error' : ''}`}
                        placeholder="email@example.com"
                        value={formData.secretary_email}
                        onChange={(e) => handleInputChange('secretary_email', e.target.value)}
                        autoComplete="email"
                      />
                      {getFieldError('secretary_email') && (
                        <span className="modern-error-text">{getFieldError('secretary_email')}</span>
                      )}
                    </div>

                    <div className="modern-form-group">
                      <label className="modern-form-label" htmlFor="member-edit-secretary-phone">
                        <span className="label-text">Phone</span>
                      </label>
                      <input
                        id="member-edit-secretary-phone"
                        type="tel"
                        className={`modern-form-input ${getFieldError('secretary_phone') ? 'error' : ''}`}
                        placeholder="Phone number"
                        value={formData.secretary_phone}
                        onChange={(e) => handleInputChange('secretary_phone', e.target.value)}
                        autoComplete="tel"
                      />
                      {getFieldError('secretary_phone') && (
                        <span className="modern-error-text">{getFieldError('secretary_phone')}</span>
                      )}
                    </div>
                  </div>
                </section>

                <section className="exec-role-card" aria-labelledby="exec-treasurer-title">
                  <h4 id="exec-treasurer-title" className="exec-role-title">Treasurer</h4>
                  <div className="exec-role-fields">
                    <div className="modern-form-group">
                      <label className="modern-form-label" htmlFor="member-edit-treasurer-name">
                        <span className="label-text">Name</span>
                      </label>
                      <input
                        id="member-edit-treasurer-name"
                        type="text"
                        className="modern-form-input"
                        placeholder="Full name"
                        value={formData.treasurer_name}
                        onChange={(e) => handleInputChange('treasurer_name', e.target.value)}
                      />
                    </div>

                    <div className="modern-form-group">
                      <label className="modern-form-label" htmlFor="member-edit-treasurer-email">
                        <span className="label-text">Email</span>
                      </label>
                      <input
                        id="member-edit-treasurer-email"
                        type="email"
                        className={`modern-form-input ${getFieldError('treasurer_email') ? 'error' : ''}`}
                        placeholder="email@example.com"
                        value={formData.treasurer_email}
                        onChange={(e) => handleInputChange('treasurer_email', e.target.value)}
                        autoComplete="email"
                      />
                      {getFieldError('treasurer_email') && (
                        <span className="modern-error-text">{getFieldError('treasurer_email')}</span>
                      )}
                    </div>

                    <div className="modern-form-group">
                      <label className="modern-form-label" htmlFor="member-edit-treasurer-phone">
                        <span className="label-text">Phone</span>
                      </label>
                      <input
                        id="member-edit-treasurer-phone"
                        type="tel"
                        className={`modern-form-input ${getFieldError('treasurer_phone') ? 'error' : ''}`}
                        placeholder="Phone number"
                        value={formData.treasurer_phone}
                        onChange={(e) => handleInputChange('treasurer_phone', e.target.value)}
                        autoComplete="tel"
                      />
                      {getFieldError('treasurer_phone') && (
                        <span className="modern-error-text">{getFieldError('treasurer_phone')}</span>
                      )}
                    </div>
                  </div>
                </section>
              </div>
            </div>

            {/* Location Information Section */}
            <div className="form-section">
              <h3 className="section-title">Location Information</h3>
              <div className="form-grid">
                <div className="modern-form-group modern-form-group--full">
                  <label className="modern-form-label">
                    <span className="label-text">Street Address 1</span>
                  </label>
                  <input
                    type="text"
                    className="modern-form-input"
                    placeholder="Enter street address"
                    value={formData.street_address_1}
                    onChange={(e) => handleInputChange('street_address_1', e.target.value)}
                  />
                </div>

                <div className="modern-form-group modern-form-group--full">
                  <label className="modern-form-label">
                    <span className="label-text">Street Address 2</span>
                  </label>
                  <input
                    type="text"
                    className="modern-form-input"
                    placeholder="Enter additional address"
                    value={formData.street_address_2}
                    onChange={(e) => handleInputChange('street_address_2', e.target.value)}
                  />
                </div>

                <div className="modern-form-group">
                  <label className="modern-form-label">
                    <span className="label-text">City</span>
                  </label>
                  <input
                    type="text"
                    className="modern-form-input"
                    placeholder="Enter city"
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                  />
                </div>

                <div className="modern-form-group">
                  <label className="modern-form-label">
                    <span className="label-text">State</span>
                  </label>
                  <input
                    type="text"
                    className="modern-form-input"
                    placeholder="Enter state"
                    value={formData.state}
                    onChange={(e) => handleInputChange('state', e.target.value)}
                  />
                </div>

                <div className="modern-form-group">
                  <label className="modern-form-label">
                    <span className="label-text">Postcode</span>
                  </label>
                  <input
                    type="text"
                    className="modern-form-input"
                    placeholder="Enter postcode"
                    value={formData.postcode}
                    onChange={(e) => handleInputChange('postcode', e.target.value)}
                  />
                </div>

                <div className="modern-form-group">
                  <label className="modern-form-label">
                    <span className="label-text">Country</span>
                  </label>
                  <input
                    type="text"
                    className="modern-form-input"
                    placeholder="Enter country"
                    value={formData.country}
                    onChange={(e) => handleInputChange('country', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Membership Information Section */}
            <div className="form-section">
              <h3 className="section-title">Membership Information</h3>
              <div className="form-grid">
                <div className="modern-form-group">
                  <label className="modern-form-label">
                    <span className="label-text">Status</span>
                  </label>
                  <select
                    className="modern-form-select"
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                  >
                    <option value="">Select Status</option>
                    <option value="approved">Approved</option>
                    <option value="pending">Pending</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>

                <div className="modern-form-group">
                  <label className="modern-form-label">
                    <span className="label-text">Membership Status</span>
                  </label>
                  <select
                    className="modern-form-select"
                    value={formData.membership_status}
                    onChange={(e) => handleInputChange('membership_status', e.target.value)}
                  >
                    <option value="">Select Membership Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                    <option value="expired">Expired</option>
                  </select>
                </div>

                <div className="modern-form-group">
                  <label className="modern-form-label">
                    <span className="label-text">Membership Start Date</span>
                  </label>
                  <input
                    type="date"
                    className="modern-form-input"
                    value={formData.membership_start_date}
                    onChange={(e) => handleInputChange('membership_start_date', e.target.value)}
                  />
                </div>

                <div className="modern-form-group">
                  <label className="modern-form-label">
                    <span className="label-text">Membership End Date</span>
                  </label>
                  <input
                    type="date"
                    className="modern-form-input"
                    value={formData.membership_end_date}
                    onChange={(e) => handleInputChange('membership_end_date', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Screening Information Section */}
            <div className="form-section">
              <h3 className="section-title">Screening Information</h3>
              <div className="form-grid">
                <div className="modern-form-group">
                  <label className="modern-form-label">
                    <span className="label-text">Screening Frequency</span>
                  </label>
                  <input
                    type="text"
                    className="modern-form-input"
                    placeholder="Enter screening frequency"
                    value={formData.screening_frequency}
                    onChange={(e) => handleInputChange('screening_frequency', e.target.value)}
                  />
                </div>

                <div className="modern-form-group">
                  <label className="modern-form-label">
                    <span className="label-text">Screening Venue</span>
                  </label>
                  <input
                    type="text"
                    className="modern-form-input"
                    placeholder="Enter screening venue"
                    value={formData.screening_venue}
                    onChange={(e) => handleInputChange('screening_venue', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Website & Social Media Section */}
            <div className="form-section">
              <h3 className="section-title">Website & Social Media</h3>
              <div className="form-grid">
                <div className="modern-form-group">
                  <label className="modern-form-label">
                    <span className="label-text">Website URL</span>
                  </label>
                  <input
                    type="url"
                    className="modern-form-input"
                    placeholder="Enter website URL"
                    value={formData.website_url}
                    onChange={(e) => handleInputChange('website_url', e.target.value)}
                  />
                </div>

                <div className="modern-form-group">
                  <label className="modern-form-label">
                    <span className="label-text">Facebook URL</span>
                  </label>
                  <input
                    type="url"
                    className="modern-form-input"
                    placeholder="Enter Facebook URL"
                    value={formData.facebook_url}
                    onChange={(e) => handleInputChange('facebook_url', e.target.value)}
                  />
                </div>

                <div className="modern-form-group">
                  <label className="modern-form-label">
                    <span className="label-text">Instagram URL</span>
                  </label>
                  <input
                    type="url"
                    className="modern-form-input"
                    placeholder="Enter Instagram URL"
                    value={formData.instagram_url}
                    onChange={(e) => handleInputChange('instagram_url', e.target.value)}
                  />
                </div>

                <div className="modern-form-group">
                  <label className="modern-form-label">
                    <span className="label-text">Twitter URL</span>
                  </label>
                  <input
                    type="url"
                    className="modern-form-input"
                    placeholder="Enter Twitter URL"
                    value={formData.twitter_url}
                    onChange={(e) => handleInputChange('twitter_url', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Settings & Permissions Section */}
            <div className="form-section">
              <h3 className="section-title">Settings & Permissions</h3>
              <div className="form-grid settings-permissions-grid">
                <div className="modern-form-group checkbox-group">
                  <label className="modern-checkbox-label">
                    <input
                      type="checkbox"
                      className="modern-checkbox"
                      checked={formData.is_active}
                      onChange={(e) => handleInputChange('is_active', e.target.checked)}
                    />
                    <span className="checkbox-text">Active Member</span>
                  </label>
                </div>


                <div className="modern-form-group checkbox-group">
                  <label className="modern-checkbox-label">
                    <input
                      type="checkbox"
                      className="modern-checkbox"
                      checked={formData.email_subscribed}
                      onChange={(e) => handleInputChange('email_subscribed', e.target.checked)}
                    />
                    <span className="checkbox-text">Email Subscribed</span>
                  </label>
                </div>

                <div className="modern-form-group checkbox-group">
                  <label className="modern-checkbox-label">
                    <input
                      type="checkbox"
                      className="modern-checkbox"
                      checked={formData.renewal}
                      onChange={(e) => handleInputChange('renewal', e.target.checked)}
                    />
                    <span className="checkbox-text">Renewal</span>
                  </label>
                </div>

                <div className="modern-form-group checkbox-group">
                  <label className="modern-checkbox-label">
                    <input
                      type="checkbox"
                      className="modern-checkbox"
                      checked={formData.agreed_to_conditions}
                      onChange={(e) => handleInputChange('agreed_to_conditions', e.target.checked)}
                    />
                    <span className="checkbox-text">Agreed to Conditions</span>
                  </label>
                </div>

                <div className="modern-form-group checkbox-group">
                  <label className="modern-checkbox-label">
                    <input
                      type="checkbox"
                      className="modern-checkbox"
                      checked={formData.release_to_third_parties}
                      onChange={(e) => handleInputChange('release_to_third_parties', e.target.checked)}
                    />
                    <span className="checkbox-text">Release to Third Parties</span>
                  </label>
                </div>

                <div className="modern-form-group checkbox-group">
                  <label className="modern-checkbox-label">
                    <input
                      type="checkbox"
                      className="modern-checkbox"
                      checked={formData.publish_on_website}
                      onChange={(e) => handleInputChange('publish_on_website', e.target.checked)}
                    />
                    <span className="checkbox-text">Publish on Website</span>
                  </label>
                </div>

                <div className="modern-form-group checkbox-group">
                  <label className="modern-checkbox-label">
                    <input
                      type="checkbox"
                      className="modern-checkbox"
                      checked={formData.show_in_external_view}
                      onChange={(e) => handleInputChange('show_in_external_view', e.target.checked)}
                    />
                    <span className="checkbox-text">Show in External View</span>
                  </label>
                </div>

                <div className="modern-form-group checkbox-group">
                  <label className="modern-checkbox-label">
                    <input
                      type="checkbox"
                      className="modern-checkbox"
                      checked={formData.allow_distributor_access}
                      onChange={(e) => handleInputChange('allow_distributor_access', e.target.checked)}
                    />
                    <span className="checkbox-text">Allow Distributor Access</span>
                  </label>
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleClose}
            disabled={isProcessing}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={isProcessing}
          >
            {isProcessing ? 'Updating...' : 'Update Member'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MemberEditModal;